/**
 * RelatedLinksPanel — fetches ~25 curated web links for a topic query
 * and renders them in a tabbed panel with per-link bookmark toggle.
 *
 * Data source: /api/related-links?q=<topic>
 *   Aggregates Wikipedia OpenSearch, DuckDuckGo web results (lite
 *   endpoint), and pinned "search elsewhere" chips. Cached 30 days.
 *
 * Bookmarks: /api/bookmarks (auth-required).
 *   Anonymous users see the links but the bookmark button prompts
 *   them to sign in.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck, ExternalLink, Loader2, Globe, Search, BookOpenText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_STYLE = {
  wikipedia: { color: "#3366cc", label: "Wikipedia", Icon: BookOpenText },
  web:       { color: "#0F172A", label: "Web",       Icon: Globe },
  search:    { color: "#7C3AED", label: "Search",    Icon: Search },
};

// Google's public favicon service — 32-px favicon for any host.
// Used as a tiny visual anchor next to each result so the panel
// scans faster.
function faviconFor(url) {
  try {
    const host = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=32`;
  } catch {
    return null;
  }
}

export default function RelatedLinksPanel({ query, testid = "related-links-panel" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [bookmarks, setBookmarks] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    if (!query) return;
    setLoading(true);
    axios.get(`${API}/api/related-links`, { params: { q: query, limit: 25 } })
      .then((r) => alive && setItems(r.data?.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [query]);

  // Load existing bookmarks so the button state is correct on mount.
  useEffect(() => {
    let alive = true;
    http.get("/bookmarks").then((r) => {
      if (!alive) return;
      const urls = new Set((r.data?.items || []).map((b) => b.url));
      setBookmarks(urls);
    }).catch(() => { /* anonymous / offline — ignore */ });
    return () => { alive = false; };
  }, []);

  const toggleBookmark = async (item) => {
    const alreadyBookmarked = bookmarks.has(item.url);
    try {
      if (alreadyBookmarked) {
        await http.delete(`/bookmarks?url=${encodeURIComponent(item.url)}`);
        setBookmarks((prev) => {
          const next = new Set(prev); next.delete(item.url); return next;
        });
        toast.success("Bookmark removed");
      } else {
        await http.post("/bookmarks", {
          url: item.url, title: item.title, snippet: item.snippet || "", tag: query,
        });
        setBookmarks((prev) => new Set(prev).add(item.url));
        toast.success("Bookmarked");
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sign in to save bookmarks.");
      } else {
        toast.error("Couldn't update bookmark");
      }
    }
  };

  const sources = Array.from(new Set(items.map((i) => i.source))).sort();
  const visible = filter === "all" ? items : items.filter((i) => i.source === filter);

  return (
    <section className="mt-8 rounded-2xl border-2 p-5" style={{ borderColor: "#DBEAFE", background: "#F8FAFF" }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Globe className="w-4 h-4" style={{ color: "#2563EB" }} />
        <h2 className="font-display text-xl">Related Links</h2>
        <span className="text-xs opacity-60">
          {loading ? "loading…" : `${items.length} sources`} · cached 30 days
        </span>
      </div>

      {/* Tabs */}
      {sources.length > 1 && (
        <div className="mb-4 flex items-center gap-1.5 flex-wrap" data-testid="related-links-tabs">
          <TabPill
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label={`All · ${items.length}`}
            testid="related-tab-all"
          />
          {sources.map((s) => {
            const style = SOURCE_STYLE[s] || SOURCE_STYLE.web;
            const count = items.filter((i) => i.source === s).length;
            return (
              <TabPill
                key={s}
                active={filter === s}
                onClick={() => setFilter(s)}
                color={style.color}
                Icon={style.Icon}
                label={`${style.label} · ${count}`}
                testid={`related-tab-${s}`}
              />
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Fetching links…</div>
      ) : visible.length === 0 ? (
        <div className="text-sm opacity-70">No links yet for this topic.</div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-2" data-testid="related-links-list">
          {visible.map((it, i) => {
            const style = SOURCE_STYLE[it.source] || SOURCE_STYLE.web;
            const bookmarked = bookmarks.has(it.url);
            const Icon = style.Icon;
            const favicon = faviconFor(it.url);
            return (
              <li key={it.url + i} className="rounded-xl border bg-white p-3 hover:shadow-sm transition flex gap-3" style={{ borderColor: "var(--wofy-border)" }}>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative overflow-hidden"
                  style={{ background: `${style.color}1a`, color: style.color }}
                >
                  {favicon ? (
                    <img
                      src={favicon}
                      alt=""
                      className="w-5 h-5"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                    />
                  ) : null}
                  <div className="absolute inset-0 flex items-center justify-center" style={{ display: favicon ? "none" : "flex" }}>
                    <Icon className="w-4 h-4" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium hover:underline break-words"
                    style={{ color: style.color }}
                    data-testid={`related-link-${i}`}
                  >
                    {it.title}
                    <ExternalLink className="inline w-3 h-3 ml-1 opacity-50" />
                  </a>
                  {it.snippet && (
                    <div className="text-xs opacity-70 mt-1 line-clamp-2">{it.snippet}</div>
                  )}
                </div>
                <button
                  onClick={() => toggleBookmark(it)}
                  className="shrink-0 opacity-60 hover:opacity-100 transition"
                  title={bookmarked ? "Remove bookmark" : "Save to bookmarks"}
                  aria-label={bookmarked ? "Remove bookmark" : "Save to bookmarks"}
                  data-testid={`related-bookmark-${i}`}
                >
                  {bookmarked
                    ? <BookmarkCheck className="w-4 h-4" style={{ color: "#B45309" }} />
                    : <Bookmark className="w-4 h-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function TabPill({ active, onClick, label, color = "#334155", Icon, testid }) {
  return (
    <button
      onClick={onClick}
      className="text-xs font-mono uppercase tracking-widest rounded-full px-3 py-1.5 border transition inline-flex items-center gap-1"
      style={active
        ? { background: color, borderColor: color, color: "#fff" }
        : { background: "#fff", borderColor: "#E2E8F0", color }}
      data-testid={testid}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </button>
  );
}
